/**
 * Business Objective Detail Route
 *
 * Displays comprehensive business objective metadata
 */

import { useState } from 'react';
import { createFileRoute, useNavigate, Outlet, useLocation } from '@tanstack/react-router';
import { useBusinessObjective } from '../../graphql/queries';
import { useApolloClient } from '@apollo/client/react';
import {
  DetailSkeleton,
  EmptyState,
  DeleteConfirmationDialog,
  PermissionGate,
} from '../../components/shared';
import { deleteBusinessObjective, ApiError } from '../../lib/apiClient';
import '@tylertech/forge/card';
import '@tylertech/forge/button';
import '@tylertech/forge/icon';
import '@tylertech/forge/divider';
import './businessObjectiveId.css';

export const Route = createFileRoute('/business-objectives/$businessObjectiveId')({
  component: BusinessObjectiveDetailPage,
});

function BusinessObjectiveDetailPage(): JSX.Element {
  const { businessObjectiveId } = Route.useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const apolloClient = useApolloClient();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // If on a child route (edit), render only the outlet
  const isChildRoute = location.pathname.endsWith('/edit');

  // Fetch business objective data
  const { data, loading, error: fetchError } = useBusinessObjective(businessObjectiveId);
  const businessObjective = data?.businessObjective;

  const CardElement = 'forge-card' as React.ElementType;
  const IconElement = 'forge-icon' as React.ElementType;
  const DividerElement = 'forge-divider' as React.ElementType;
  const ForgeButton = 'forge-button' as React.ElementType;

  if (isChildRoute) {
    return <Outlet />;
  }

  // Loading state
  if (loading) {
    return (
      <div className="business-objective-detail-page">
        <DetailSkeleton cards={5} />
      </div>
    );
  }

  // Error state
  if (fetchError) {
    return (
      <div className="business-objective-detail-page">
        <EmptyState
          icon="error"
          title="Failed to load business objective"
          description={fetchError.message}
          actionLabel="Back to Business Objectives"
          onAction={() =>
            navigate({ to: '/business-objectives', search: { page: 1, pageSize: 25 } })
          }
        />
      </div>
    );
  }

  // Not found state
  if (!businessObjective) {
    return (
      <div className="business-objective-detail-page">
        <EmptyState
          icon="search_off"
          title="Business objective not found"
          description={`No business objective found with ID: ${businessObjectiveId}`}
          actionLabel="Back to Business Objectives"
          onAction={() =>
            navigate({ to: '/business-objectives', search: { page: 1, pageSize: 25 } })
          }
        />
      </div>
    );
  }

  // Format dates
  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Handle edit action
  const handleEdit = (): void => {
    navigate({
      to: `/business-objectives/${businessObjective.entityId}/edit` as string,
    });
  };

  // Handle delete action
  const handleDelete = (): void => {
    setDeleteDialogOpen(true);
  };

  // Confirm delete
  const handleConfirmDelete = async (): Promise<void> => {
    setIsDeleting(true);
    setError(null);

    try {
      // Soft delete by default
      await deleteBusinessObjective(businessObjectiveId, false);

      // Refetch GraphQL data to update cache
      await apolloClient.refetchQueries({
        include: ['ListBusinessObjectives'],
      });

      // Navigate back to business objectives list
      navigate({ to: '/business-objectives', search: { page: 1, pageSize: 25 } });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
      setIsDeleting(false);
    }
  };

  return (
    <div className="business-objective-detail-page">
      {/* Header */}
      <div className="business-objective-detail-header">
        <div>
          <ForgeButton
            type="button"
            variant="text"
            onClick={() =>
              navigate({ to: '/business-objectives', search: { page: 1, pageSize: 25 } })
            }
          >
            <IconElement name="arrow_back" aria-hidden="true" />
            Back to Business Objectives
          </ForgeButton>
          <h1 className="forge-typography--headline4">{businessObjective.name}</h1>
          <p className="forge-typography--body2 page-header-subtitle">
            {businessObjective.description || 'No description provided'}
          </p>
        </div>
        <div className="business-objective-detail-actions">
          <PermissionGate permission="business-objective:update">
            <ForgeButton type="button" variant="raised" onClick={handleEdit}>
              <IconElement name="edit" aria-hidden="true" />
              Edit
            </ForgeButton>
          </PermissionGate>
          <PermissionGate permission="business-objective:delete">
            <ForgeButton type="button" variant="outlined" onClick={handleDelete}>
              <IconElement name="delete" aria-hidden="true" />
              Delete
            </ForgeButton>
          </PermissionGate>
        </div>
      </div>

      <div className="business-objective-detail-content">
        {/* Overview Card */}
        <CardElement className="detail-card">
          <div className="card-header">
            <h2 className="forge-typography--headline6">Overview</h2>
          </div>
          <DividerElement />
          <div className="detail-grid">
            <div className="detail-row">
              <span className="detail-label">Business Objective ID</span>
              <span className="detail-value">{businessObjective.entityId}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Objective Type</span>
              <span className="detail-value">{businessObjective.objectiveType || '—'}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Priority</span>
              <span className="detail-value">{businessObjective.priority || '—'}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Status</span>
              <span className="detail-value">{businessObjective.status || '—'}</span>
            </div>
          </div>
        </CardElement>

        {/* Timeline Card */}
        <CardElement className="detail-card">
          <div className="card-header">
            <h2 className="forge-typography--headline6">Timeline</h2>
          </div>
          <DividerElement />
          <div className="detail-grid">
            <div className="detail-row">
              <span className="detail-label">Start Date</span>
              <span className="detail-value">{businessObjective.startDate || '—'}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Target Date</span>
              <span className="detail-value">{businessObjective.targetDate || '—'}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Time Horizon</span>
              <span className="detail-value">{businessObjective.timeHorizon || '—'}</span>
            </div>
          </div>
        </CardElement>

        {/* Ownership Card */}
        <CardElement className="detail-card">
          <div className="card-header">
            <h2 className="forge-typography--headline6">Ownership</h2>
          </div>
          <DividerElement />
          <div className="detail-grid">
            <div className="detail-row">
              <span className="detail-label">Owner ID</span>
              <span className="detail-value">{businessObjective.ownerId || '—'}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Owning Business Unit ID</span>
              <span className="detail-value">{businessObjective.owningBusinessUnitId || '—'}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Sponsor ID</span>
              <span className="detail-value">{businessObjective.sponsorId || '—'}</span>
            </div>
          </div>
        </CardElement>

        {/* Goals Card */}
        <CardElement className="detail-card">
          <div className="card-header">
            <h2 className="forge-typography--headline6">Goals</h2>
          </div>
          <DividerElement />
          <div className="detail-grid">
            <div className="detail-row">
              <span className="detail-label">Success Criteria</span>
              <span className="detail-value">{businessObjective.successCriteria || '—'}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">KPI Statement</span>
              <span className="detail-value">{businessObjective.kpiStatement || '—'}</span>
            </div>
          </div>
        </CardElement>

        {/* Additional Information Card */}
        <CardElement className="detail-card">
          <div className="card-header">
            <h2 className="forge-typography--headline6">Additional Information</h2>
          </div>
          <DividerElement />
          <div className="detail-grid">
            <div className="detail-row">
              <span className="detail-label">Tags</span>
              <span className="detail-value">
                {businessObjective.tags && businessObjective.tags.length > 0
                  ? businessObjective.tags.join(', ')
                  : '—'}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Notes</span>
              <span className="detail-value">{businessObjective.notes || '—'}</span>
            </div>
          </div>
        </CardElement>

        {/* Metadata Card */}
        <CardElement className="detail-card">
          <div className="card-header">
            <h2 className="forge-typography--headline6">Metadata</h2>
          </div>
          <DividerElement />
          <div className="detail-grid">
            <div className="detail-row">
              <span className="detail-label">Created</span>
              <span className="detail-value">
                {formatDate(businessObjective._createdAt)} by {businessObjective._createdBy}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Last Updated</span>
              <span className="detail-value">
                {formatDate(businessObjective._updatedAt)} by {businessObjective._updatedBy}
              </span>
            </div>
          </div>
        </CardElement>
      </div>

      {/* Delete Confirmation Dialog */}
      {deleteDialogOpen && (
        <DeleteConfirmationDialog
          open={deleteDialogOpen}
          onConfirm={handleConfirmDelete}
          onCancel={() => setDeleteDialogOpen(false)}
          title="Delete Business Objective"
          message="Are you sure you want to delete this business objective?"
          warning="This action will soft delete the business objective. It can be recovered by an administrator."
          itemName={businessObjective.name}
          isDeleting={isDeleting}
        />
      )}

      {/* Error Banner */}
      {error && <div className="detail-error-toast">{error}</div>}
    </div>
  );
}
